import { useEffect, useState } from "react";
import type { MainStrategyEnum } from "../../apiClient/generated";
import { ApiValidationError } from "./api";
import {
    type EditorMetadata,
    type LinkingRule,
    linkingRuleProviderLabel,
    linkingRuleProviderValue as providerValue,
    type WorkflowDefinition,
} from "./types";
import type { EditorMutations } from "./useEditorMutations";

/**
 * The definition's own fields, and its event-linking rules.
 *
 * Owner is not editable: moving a definition between companies would invalidate every reference
 * param configured on it without saying so. Duplicating to another company is the supported route
 * and clears them deliberately — that is still done from the admin change form.
 */
export function DefinitionForm({
    definition,
    metadata,
    mutations,
    onDirtyChange,
}: {
    definition: WorkflowDefinition;
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
}) {
    const [name, setName] = useState(definition.name);
    const [description, setDescription] = useState(definition.description);
    const [isActive, setIsActive] = useState(definition.isActive);
    const [autoLinking, setAutoLinking] = useState(definition.autoEventLinking);

    useEffect(() => {
        setName(definition.name);
        setDescription(definition.description);
        setIsActive(definition.isActive);
        setAutoLinking(definition.autoEventLinking);
    }, [
        definition.name,
        definition.description,
        definition.isActive,
        definition.autoEventLinking,
    ]);

    const isDirty =
        name !== definition.name ||
        description !== definition.description ||
        isActive !== definition.isActive ||
        autoLinking !== definition.autoEventLinking;

    useEffect(() => {
        onDirtyChange("definition", isDirty);
        return () => onDirtyChange("definition", false);
    }, [isDirty, onDirtyChange]);

    const save = mutations.saveDefinition;
    const fieldErrors =
        save.error instanceof ApiValidationError ? save.error.fieldErrors : {};

    return (
        <section className="aer-wf-section">
            <h3>Definition</h3>

            <div className="aer-wf-fields">
                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-name">Name</label>
                    <input
                        id="aer-wf-name"
                        type="text"
                        maxLength={50}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                    {fieldErrors.name && (
                        <p className="aer-wf-field__error" role="alert">
                            {fieldErrors.name.join(" ")}
                        </p>
                    )}
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-description">Description</label>
                    <textarea
                        id="aer-wf-description"
                        rows={2}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                </div>

                <div className="aer-wf-field aer-wf-field--inline">
                    <input
                        id="aer-wf-active"
                        type="checkbox"
                        checked={isActive}
                        onChange={(event) => setIsActive(event.target.checked)}
                    />
                    <label htmlFor="aer-wf-active">Active</label>
                    <p className="aer-wf-field__hint">
                        Inactive definitions are not used for new workflows.
                    </p>
                </div>

                <div className="aer-wf-field aer-wf-field--inline">
                    <input
                        id="aer-wf-linking"
                        type="checkbox"
                        checked={autoLinking}
                        onChange={(event) =>
                            setAutoLinking(event.target.checked)
                        }
                    />
                    <label htmlFor="aer-wf-linking">
                        Automatic event linking
                    </label>
                </div>
            </div>

            <div className="aer-wf-rule__save">
                <button
                    type="button"
                    className="aer-wf-button"
                    disabled={!isDirty || save.isLoading}
                    onClick={() =>
                        save.mutate({
                            name,
                            description,
                            isActive: isActive,
                            autoEventLinking: autoLinking,
                        })
                    }
                >
                    {save.isLoading ? "Saving…" : "Save definition"}
                </button>
                {isDirty && <span className="aer-wf-rule__dirty">Unsaved</span>}
            </div>

            {/* Keyed off the draft, not the saved value: someone who has just ticked the box
                expects the rules to configure, and a rule can exist perfectly well before
                linking is switched on. */}
            {autoLinking && (
                <LinkingRulesForm
                    definition={definition}
                    metadata={metadata}
                    mutations={mutations}
                    onDirtyChange={onDirtyChange}
                    linkingUnsaved={autoLinking !== definition.autoEventLinking}
                />
            )}
        </section>
    );
}

/**
 * The definition's linking rules: one per data provider, plus at most one for all providers.
 *
 * A provider's own rule takes precedence, so the all-providers rule only covers providers that
 * have no rule of their own.
 */
function LinkingRulesForm({
    definition,
    metadata,
    mutations,
    onDirtyChange,
    linkingUnsaved,
}: {
    definition: WorkflowDefinition;
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
    linkingUnsaved: boolean;
}) {
    const rules = definition.linkingRules;
    const [adding, setAdding] = useState(false);
    const takenProviders = rules.map(providerValue);
    const allTaken = [
        "",
        ...metadata.dataProviders.map((option) => option.value),
    ].every((value) => takenProviders.includes(value));

    // An open add form is abandoned once there is nothing left to add, so it does not reappear
    // unasked when a rule is later removed.
    useEffect(() => {
        if (allTaken) setAdding(false);
    }, [allTaken]);

    return (
        <div className="aer-wf-subsection">
            <h4>Event linking</h4>

            {linkingUnsaved && (
                <p className="aer-wf-field__hint aer-wf-field__hint--warn">
                    Save the definition to switch automatic linking on. The
                    rules below can be configured either way.
                </p>
            )}

            {rules.length > 1 && (
                <p className="aer-wf-field__hint">
                    A provider's own rule takes precedence: the all-providers
                    rule only applies to providers without one.
                </p>
            )}

            {rules.map((rule) => (
                <LinkingRuleFields
                    key={rule.id}
                    rule={rule}
                    // A provider can only have one rule, so the others' providers are not offered.
                    takenProviders={rules
                        .filter((other) => other.id !== rule.id)
                        .map(providerValue)}
                    metadata={metadata}
                    mutations={mutations}
                    onDirtyChange={onDirtyChange}
                />
            ))}

            {/* Every provider, and "all providers", already has a rule: there is nothing left
                to add, so say why instead of offering a form that can never be submitted. */}
            {allTaken ? (
                <p className="aer-wf-field__hint">
                    Every data provider already has a rule, including the
                    all-providers rule. Remove a rule or change its provider to
                    add another.
                </p>
            ) : rules.length === 0 || adding ? (
                <NewLinkingRuleForm
                    hasRules={rules.length > 0}
                    takenProviders={takenProviders}
                    metadata={metadata}
                    mutations={mutations}
                    onDone={() => setAdding(false)}
                />
            ) : (
                <div className="aer-wf-rule__save">
                    <button
                        type="button"
                        className="aer-wf-button aer-wf-button--secondary"
                        onClick={() => setAdding(true)}
                    >
                        Add linking rule
                    </button>
                </div>
            )}
        </div>
    );
}

function ProviderOptions({
    metadata,
    takenProviders,
}: {
    metadata: EditorMetadata;
    takenProviders: string[];
}) {
    return (
        <>
            <option value="" disabled={takenProviders.includes("")}>
                All providers
            </option>
            {metadata.dataProviders.map((option) => (
                <option
                    key={option.value}
                    value={option.value}
                    disabled={takenProviders.includes(option.value)}
                >
                    {option.label}
                </option>
            ))}
        </>
    );
}

/**
 * Create a linking rule, either the first one or one for another provider.
 *
 * Worth having rather than linking out to the admin: automatic linking with no rule is a state
 * the engine handles by logging a warning and skipping linking entirely, so it looks configured
 * and does nothing.
 */
function NewLinkingRuleForm({
    hasRules,
    takenProviders,
    metadata,
    mutations,
    onDone,
}: {
    hasRules: boolean;
    takenProviders: string[];
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDone: () => void;
}) {
    const firstFreeProvider =
        ["", ...metadata.dataProviders.map((option) => option.value)].find(
            (value) => !takenProviders.includes(value),
        ) ?? "";
    const [provider, setProvider] = useState(firstFreeProvider);
    const [strategy, setStrategy] = useState<MainStrategyEnum>(
        (metadata.linkingStrategies[0]?.value as MainStrategyEnum) ?? "oldest",
    );
    const [fieldsText, setFieldsText] = useState("");

    const add = mutations.addLinkingRule;
    const fieldErrors =
        add.error instanceof ApiValidationError ? add.error.fieldErrors : {};

    const parsedFields = fieldsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    return (
        <div className="aer-wf-rule aer-wf-rule--linking">
            {hasRules ? (
                <div className="aer-wf-rule__header">
                    <span className="aer-wf-rule__ordinal">
                        New linking rule
                    </span>
                </div>
            ) : (
                <div className="aer-wf-notice aer-wf-notice--warn">
                    <p>
                        No linking rule is configured. Until there is one, the
                        engine logs a warning and skips linking — nothing is
                        grouped.
                    </p>
                </div>
            )}

            <div className="aer-wf-fields">
                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-new-strategy">Main event</label>
                    <select
                        id="aer-wf-new-strategy"
                        value={strategy}
                        onChange={(event) =>
                            setStrategy(event.target.value as MainStrategyEnum)
                        }
                    >
                        {metadata.linkingStrategies.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-new-provider">Data provider</label>
                    <select
                        id="aer-wf-new-provider"
                        value={provider}
                        onChange={(event) => setProvider(event.target.value)}
                    >
                        <ProviderOptions
                            metadata={metadata}
                            takenProviders={takenProviders}
                        />
                    </select>
                    {/* The default was free when the form opened, but another rule can take it
                        before this one is created. */}
                    {takenProviders.includes(provider) && (
                        <p className="aer-wf-field__hint aer-wf-field__hint--warn">
                            {linkingRuleProviderLabel(
                                metadata.dataProviders,
                                provider,
                            )}{" "}
                            already has a rule. Choose another provider.
                        </p>
                    )}
                    {fieldErrors.data_provider && (
                        <p className="aer-wf-field__error" role="alert">
                            {fieldErrors.data_provider.join(" ")}
                        </p>
                    )}
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-new-grouping">Grouping fields</label>
                    <textarea
                        id="aer-wf-new-grouping"
                        rows={3}
                        value={fieldsText}
                        onChange={(event) => setFieldsText(event.target.value)}
                    />
                    <p className="aer-wf-field__hint">
                        One field path per line, e.g. <code>site_id</code> or{" "}
                        <code>data_point.data.custom_field</code>.
                    </p>
                    {fieldErrors.grouping_fields && (
                        <p className="aer-wf-field__error" role="alert">
                            {fieldErrors.grouping_fields.join(" ")}
                        </p>
                    )}
                </div>
            </div>

            {fieldErrors.workflow && (
                <p className="aer-wf-field__error" role="alert">
                    {fieldErrors.workflow.join(" ")}
                </p>
            )}

            <div className="aer-wf-rule__save">
                <button
                    type="button"
                    className="aer-wf-button"
                    disabled={
                        !parsedFields.length ||
                        takenProviders.includes(provider) ||
                        add.isLoading
                    }
                    onClick={() =>
                        add.mutate(
                            {
                                dataProvider: provider || null,
                                mainStrategy: strategy,
                                groupingFields: parsedFields,
                            },
                            { onSuccess: onDone },
                        )
                    }
                >
                    {add.isLoading ? "Creating…" : "Create linking rule"}
                </button>
                {hasRules && (
                    <button
                        type="button"
                        className="aer-wf-button aer-wf-button--secondary"
                        onClick={() => {
                            add.reset();
                            onDone();
                        }}
                    >
                        Cancel
                    </button>
                )}
                {!parsedFields.length && (
                    <span className="aer-wf-field__hint">
                        At least one grouping field is needed — it is what
                        decides which events belong together.
                    </span>
                )}
            </div>
        </div>
    );
}

function LinkingRuleFields({
    rule,
    takenProviders,
    metadata,
    mutations,
    onDirtyChange,
}: {
    rule: LinkingRule;
    takenProviders: string[];
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
}) {
    const [provider, setProvider] = useState(providerValue(rule));
    const [strategy, setStrategy] = useState(rule.mainStrategy);
    // Edited as one path per line: the stored value is a JSON array of field paths, and a raw
    // JSON textarea is a worse editing experience than a list.
    const [fieldsText, setFieldsText] = useState(
        (rule.groupingFields ?? []).join("\n"),
    );

    useEffect(() => {
        setProvider(rule.dataProvider ?? "");
        setStrategy(rule.mainStrategy);
        setFieldsText((rule.groupingFields ?? []).join("\n"));
    }, [rule.dataProvider, rule.mainStrategy, rule.groupingFields]);

    const parsedFields = fieldsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    const isDirty =
        provider !== providerValue(rule) ||
        strategy !== rule.mainStrategy ||
        parsedFields.join("\n") !== (rule.groupingFields ?? []).join("\n");

    const dirtyKey = `linking-rule-${rule.id}`;
    useEffect(() => {
        onDirtyChange(dirtyKey, isDirty);
        return () => onDirtyChange(dirtyKey, false);
    }, [dirtyKey, isDirty, onDirtyChange]);

    // The save and remove mutations are shared by every rule, so their state only belongs to
    // this rule when it was this rule they ran for.
    const save = mutations.saveLinkingRule;
    const saving = save.isLoading && save.variables?.id === rule.id;
    const fieldErrors =
        save.error instanceof ApiValidationError &&
        save.variables?.id === rule.id
            ? save.error.fieldErrors
            : {};
    const remove = mutations.removeLinkingRule;
    const removing = remove.isLoading && remove.variables === rule.id;

    const idPrefix = `aer-wf-rule-${rule.id}`;

    return (
        <div className="aer-wf-rule aer-wf-rule--linking">
            <div className="aer-wf-rule__header">
                <span className="aer-wf-rule__ordinal">
                    {linkingRuleProviderLabel(
                        metadata.dataProviders,
                        providerValue(rule),
                    )}
                </span>
            </div>

            {/* Grouping fields that match no data are informative, not fatal. */}
            {rule.warnings.length > 0 && (
                <div className="aer-wf-notice aer-wf-notice--warn">
                    <ul>
                        {rule.warnings.map((warning) => (
                            <li key={warning}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="aer-wf-fields">
                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-provider`}>
                        Data provider
                    </label>
                    <select
                        id={`${idPrefix}-provider`}
                        value={provider}
                        onChange={(event) => setProvider(event.target.value)}
                    >
                        <ProviderOptions
                            metadata={metadata}
                            takenProviders={takenProviders}
                        />
                    </select>
                    {fieldErrors.data_provider && (
                        <p className="aer-wf-field__error" role="alert">
                            {fieldErrors.data_provider.join(" ")}
                        </p>
                    )}
                </div>

                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-strategy`}>Main event</label>
                    <select
                        id={`${idPrefix}-strategy`}
                        value={strategy}
                        onChange={(event) =>
                            setStrategy(event.target.value as MainStrategyEnum)
                        }
                    >
                        {metadata.linkingStrategies.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-grouping`}>
                        Grouping fields
                    </label>
                    <textarea
                        id={`${idPrefix}-grouping`}
                        rows={3}
                        value={fieldsText}
                        onChange={(event) => setFieldsText(event.target.value)}
                    />
                    <p className="aer-wf-field__hint">
                        One field path per line, e.g. <code>site_id</code> or{" "}
                        <code>data_point.data.custom_field</code>.
                    </p>
                    {fieldErrors.grouping_fields && (
                        <p className="aer-wf-field__error" role="alert">
                            {fieldErrors.grouping_fields.join(" ")}
                        </p>
                    )}
                </div>
            </div>

            <div className="aer-wf-rule__save">
                <button
                    type="button"
                    className="aer-wf-button"
                    disabled={!isDirty || saving}
                    onClick={() =>
                        save.mutate({
                            id: rule.id,
                            fields: {
                                dataProvider: provider || null,
                                mainStrategy: strategy,
                                groupingFields: parsedFields,
                            },
                        })
                    }
                >
                    {saving ? "Saving…" : "Save linking rule"}
                </button>
                {isDirty && <span className="aer-wf-rule__dirty">Unsaved</span>}
                <button
                    type="button"
                    className="aer-wf-button aer-wf-button--secondary"
                    disabled={removing}
                    onClick={() => remove.mutate(rule.id)}
                >
                    Remove linking rule
                </button>
            </div>
            <p className="aer-wf-field__hint">
                Turning automatic linking off leaves this rule in place — it
                simply stops being used. Remove it to delete it.
            </p>
        </div>
    );
}
